<template>
  <div class="compare-summary">
    <div class="compare-summary-header">
      <div class="compare-summary-title">Compare Summary</div>
      <div class="compare-summary-subtitle">
        Quick highlights from the selected BOM version comparison.
      </div>
    </div>
    <div class="grid-2">
      <div class="compare-summary-diff">
        <div class="compare-summary-diff-card">
          <div class="compare-summary-diff-card-head">
            <div class="compare-summary-diff-card-title">Changes detected</div>
            <div class="compare-summary-diff-card-count">{{ visibleDiffList.length }}</div>
          </div>
          <div
            v-for="diff in visibleDiffList"
            :key="diff.id"
            :class="{
              'compare-summary-diff-list-item': true,
              'compare-summary-diff-list-item--add': diff.isAdded,
              'compare-summary-diff-list-item--remove': diff.isDeleted,
              'compare-summary-diff-list-item--update': diff.isUpdated,
            }"
          >
            <div class="compare-summary-diff-list-item-badge">
              {{ getDiffType(diff) }}
            </div>
            <ul class="compare-summary-diff-list">
              <li v-for="msg in diff.message" :key="msg">{{ msg }}</li>
            </ul>
          </div>
        </div>
      </div>
      <div class="compare-summary-kpi" >
        <kpi-card title="Bom Similarity" :value="getSimilarity" />
        <kpi-card title="Updated Material" :value="updatedPer" color="#ffd100" />
        <kpi-card title="Newly Added Material" :value="addedPer" color="#11ab0b" />
        <kpi-card title="Removed Material" :value="deletedPer" color="#ff0000" />
      </div>
    </div>
  </div>
</template>

<script>
import kpiCard from './kpiCard.vue'
export default {
  components: { kpiCard },
  name: 'CompareSummery',
  props: {
    diffList: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    visibleDiffList () {
      return this.diffList.filter((diff) => {
        return (
          diff.isAdded ||
          diff.isDeleted ||
          diff.isUpdated ||
          (Array.isArray(diff.message) && diff.message.length > 0)
        )
      })
    },
    getSimilarity () {
      /*
      * For each material it has score of 5 = material itself + 4 fields
      * fields are associated BOM, price, quantity, unit
      */
      let totalScore = 0
      let diffScore = 0
      const MATERIAL_SCORE = 5
      this.diffList.forEach((diff) => {
        totalScore += MATERIAL_SCORE
        if (diff.isAdded || diff.isDeleted) {
          diffScore += MATERIAL_SCORE
        } else {
          diffScore += diff.message.length
        }
      })
      return totalScore
        ? (((totalScore - diffScore) / totalScore) * 100).toFixed(2)
        : 100
    },
    deletedPer () {
      const oldList = this.diffList.filter((diff) => !diff.isAdded) || []
      const deleted = this.diffList.filter((diff) => diff.isDeleted)
      return oldList.length
        ? ((deleted.length / oldList.length) * 100).toFixed(2)
        : 0
    },
    addedPer () {
      const newList = this.diffList.filter((diff) => !diff.isDeleted) || []
      const added = this.diffList.filter((diff) => diff.isAdded)
      return newList.length
        ? ((added.length / newList.length) * 100).toFixed(2)
        : 0
    },
    updatedPer () {
      const oldList = this.diffList.filter((diff) => !diff.isAdded) || []
      const updated = this.diffList.filter((diff) => diff.isUpdated)
      return oldList.length
        ? ((updated.length / oldList.length) * 100).toFixed(2)
        : 0
    }
  },
  methods: {
    getDiffType (diff) {
      if (diff.isAdded) return 'Added'
      if (diff.isDeleted) return 'Removed'
      if (diff.isUpdated) return 'Updated'
      return 'Changed'
    }
  }
}
</script>

<style lang="scss" scoped >
.compare-summary {
  width: 100%;
  border-bottom: 1px solid #e8e8e8;
  padding: 18px 0 22px;

  .grid-2 {
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(320px, 1fr);
    gap: 24px;
    align-items: start;
    margin-top: 14px;
  }

  &-header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &-title {
    font-size: var(--text-xl);
    font-weight: 400;
    color: var(--text-color-1);
    margin-bottom: 0;
  }

  &-subtitle {
    font-size: var(--text-s);
    color: rgba(56, 51, 50, 0.62);
  }

  &-diff {
    padding-right: 8px;
    max-height: 280px;

    &-card {
      background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(255, 251, 247, 0.98));
      border: 1px solid rgba(56, 51, 50, 0.08);
      border-radius: 14px;
      box-shadow: 0 6px 16px rgba(56, 51, 50, 0.05);
      padding: 16px 18px;
      max-height: 280px;
      overflow-y: auto;
    }

    &-card-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 14px;
      padding-bottom: 10px;
      border-bottom: 1px solid rgba(56, 51, 50, 0.07);
    }

    &-card-title {
      font-size: var(--text-l);
      font-weight: 400;
      color: var(--text-color);
    }

    &-card-count {
      min-width: 30px;
      height: 30px;
      padding: 0 10px;
      border-radius: 999px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: var(--text-xs2);
      font-weight: 700;
      color: var(--text-color-1);
      background: rgba(8, 36, 62, 0.08);
    }

    &-list {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding-left: 0;
      margin: 0;

      li {
        list-style: none;
        position: relative;
        padding-left: 14px;
        line-height: 1.45;

        &::before {
          content: "";
          position: absolute;
          top: 8px;
          left: 0;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: currentColor;
        }
      }

      &-item {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 12px;
        align-items: flex-start;
        padding: 12px 0;
        font-size: var(--text-m);
        border-bottom: 1px solid rgba(56, 51, 50, 0.06);

        &:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        &:first-child {
          padding-top: 0;
        }

        &-badge {
          min-width: 72px;
          padding: 5px 10px;
          border-radius: 999px;
          font-size: var(--text-xs2);
          font-weight: 700;
          text-align: center;
          background: rgba(8, 36, 62, 0.06);
          color: var(--text-color-1);
        }

        &--add {
          color: #52c41a;

          .compare-summary-diff-list-item-badge {
            background: rgba(82, 196, 26, 0.12);
            color: #389e0d;
          }
        }

        &--remove {
          color: #f5222d;

          .compare-summary-diff-list-item-badge {
            background: rgba(245, 34, 45, 0.12);
            color: #cf1322;
          }
        }

        &--update {
          color: #cf8a00;

          .compare-summary-diff-list-item-badge {
            background: rgba(207, 138, 0, 0.14);
            color: #ad6800;
          }
        }
      }
    }
  }
  &-kpi {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
}

@media (max-width: 1080px) {
  .compare-summary {
    .grid-2 {
      grid-template-columns: 1fr;
    }

    &-kpi {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}

@media (max-width: 640px) {
  .compare-summary {
    &-kpi {
      grid-template-columns: 1fr;
    }
  }
}
</style>
